/**
 * 动态API工具类
 * 提供基于配置的动态API调用功能
 */

import requests from '@/utils/request.js'
import { getApiUrl } from '@/config/apiConfig.js'

/**
 * 动态API调用函数
 * @param {string} module - 模块名称
 * @param {string} apiName - API名称
 * @param {object} data - 请求数据
 * @param {object} options - 请求选项
 * @returns {Promise} API请求Promise
 */
export const callDynamicAPI = (module, apiName, data = {}, options = {}) => {
  const url = getApiUrl(module, apiName)
  
  if (!url) {
    return Promise.reject(new Error(`API配置不存在: ${module}.${apiName}`))
  }

  const requestConfig = {
    method: options.method || 'POST',
    url: url.startsWith('/') ? url : `/${url}`,
    data
  }

  // 添加额外的请求配置
  if (options.headers) {
    requestConfig.headers = options.headers
  }
  
  if (options.timeout) {
    requestConfig.timeout = options.timeout
  }

  return requests(requestConfig)
}

/**
 * 创建模块API函数集合
 * @param {string} module - 模块名称
 * @returns {object} 模块API函数集合
 */
export const createModuleAPI = (module) => {
  const apiFunctions = {}
  
  return new Proxy(apiFunctions, {
    get(target, prop) {
      if (typeof prop === 'string') {
        return (data = {}, options = {}) => {
          return callDynamicAPI(module, prop, data, options)
        }
      }
      return target[prop]
    }
  })
}

/**
 * 批量创建模块API
 * @param {string[]} modules - 模块名称数组
 * @returns {object} 所有模块API函数集合
 */
export const createBatchModuleAPI = (modules) => {
  const allApis = {}
  
  modules.forEach(module => {
    allApis[module] = createModuleAPI(module)
  })
  
  return allApis
}

/**
 * 基于菜单配置的API调用
 * @param {object} menuConfig - 菜单配置对象
 * @param {object} data - 请求数据
 * @param {object} options - 请求选项
 * @returns {Promise} API请求Promise
 */
export const callMenuAPI = (menuConfig, data = {}, options = {}) => {
  if (!menuConfig || !menuConfig.api_URL) {
    return Promise.reject(new Error('菜单配置中缺少api_URL'))
  }

  const requestConfig = {
    method: options.method || 'POST',
    url: menuConfig.api_URL.startsWith('/') 
      ? menuConfig.api_URL 
      : `/${menuConfig.api_URL}`,
    data
  }

  // 添加额外的请求配置
  if (options.headers) {
    requestConfig.headers = options.headers
  }
  
  if (options.timeout) {
    requestConfig.timeout = options.timeout
  }

  return requests(requestConfig)
}

/**
 * API配置验证器
 * @param {string} module - 模块名称
 * @param {string} apiName - API名称
 * @returns {boolean} 配置是否存在
 */
export const validateApiConfig = (module, apiName) => {
  const url = getApiUrl(module, apiName)
  return !!url
}

/**
 * 获取API调用统计信息
 * @returns {object} 统计信息
 */
export const getApiStats = () => {
  // 这里可以添加API调用统计逻辑
  return {
    totalCalls: 0,
    successCalls: 0,
    errorCalls: 0,
    avgResponseTime: 0
  }
}

/**
 * API缓存管理器
 */
export const apiCache = {
  cache: new Map(),
  
  set(key, value, ttl = 300000) { // 默认5分钟缓存
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl
    })
  },
  
  get(key) {
    const item = this.cache.get(key)
    if (!item) return null
    
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key)
      return null
    }
    
    return item.value
  },
  
  clear() {
    this.cache.clear()
  },
  
  delete(key) {
    this.cache.delete(key)
  }
}

/**
 * 带缓存的API调用
 * @param {string} module - 模块名称
 * @param {string} apiName - API名称
 * @param {object} data - 请求数据
 * @param {object} options - 请求选项
 * @param {boolean} useCache - 是否使用缓存
 * @returns {Promise} API请求Promise
 */
export const callCachedAPI = (module, apiName, data = {}, options = {}, useCache = true) => {
  const cacheKey = `${module}.${apiName}.${JSON.stringify(data)}`
  
  if (useCache) {
    const cachedResult = apiCache.get(cacheKey)
    if (cachedResult) {
      return Promise.resolve(cachedResult)
    }
  }
  
  return callDynamicAPI(module, apiName, data, options)
    .then(result => {
      if (useCache && options.cacheTtl !== 0) {
        apiCache.set(cacheKey, result, options.cacheTtl)
      }
      return result
    })
}

export default {
  callDynamicAPI,
  createModuleAPI,
  createBatchModuleAPI,
  callMenuAPI,
  validateApiConfig,
  getApiStats,
  apiCache,
  callCachedAPI
}
